import { FormApi } from "final-form";
import { useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import authService, { LoginRequestError } from "src/services/authService";
import { getSettings } from "src/services/configService";
import { useSelector } from "src/store";
import { selectHasValidSession } from "src/store/auth";

import { unpackError } from "@dashboard/devices/api/helpers";
import { Box, InputAdornment, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { CircularProgressBox } from "@shared/CircularProgressBox";
import { PrimaryButton } from "@shared/CustomButton";
import PasswordField from "@shared/form/PasswordField";
import { TextField } from "@shared/form/TextField";
import OtpSection from "@shared/otp/OtpSection";
import { TechnicalDifficultiesFallback } from "@shared/TechnicalDifficultiesFallback";
import { showErrorToast } from "@shared/toasts/Toasts";

import { AuthFlowContainer } from "../AuthFlowContainer";

import { useStyles } from "./LoginPage.style";

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

type FormValues = {
    email: string;
    password: string;
    totp: string;
    mode: "2fa" | "password";
};
const initialValues: FormValues = {
    email: "",
    password: "",
    totp: "",
    mode: "password",
};

type RedirectDecision = { type: "internal"; url: string } | { type: "seed"; returnUrl: string } | null;

const LoginPage = () => {
    const { classes } = useStyles();
    const { features } = getSettings();
    const { t } = useTranslation(["pages", "common"]);
    const hasValidSession = useSelector(selectHasValidSession);
    const [searchParams] = useSearchParams();

    const [authMode, setAuthMode] = useState<"password" | "2fa">("password");
    const [seedError, setSeedError] = useState(false);

    useEffect(() => {
        if (!searchParams.has("client_id")) {
            return;
        }
        const noticeKey = authService.consumeLoginNotice();
        if (noticeKey === "pages:loginPage.toasts.sessionExpired") {
            showErrorToast(t("pages:loginPage.toasts.sessionExpired"));
        }
    }, [searchParams, t]);

    // Decide whether we should redirect away from the login form before rendering it.
    // - "internal": a same-app route, rendered declaratively via <Navigate>.
    // - "seed": imperative window.location side effect, run in the effect below.
    // - null: show the login form.
    const redirect = useMemo((): RedirectDecision => {
        const returnUrl = searchParams.get("returnUrl");

        if (searchParams.has("client_id")) {
            return null;
        }

        if (hasValidSession) {
            // Already authenticated: an internal returnUrl can be honored directly. An external one
            // (e.g. back to the forum) still needs a fresh OAuth round-trip — the IdP recognizes the
            // existing session and approves instantly, but the token exchange it triggers is what
            // refreshes the shared session cookie the forum relies on. A plain client-side redirect
            // would skip that exchange and leave the forum looking at a stale/missing cookie.
            const safe = returnUrl ? authService.resolveSafeReturnUrl(returnUrl) : null;
            if (safe?.kind === "external") {
                return { type: "seed", returnUrl: safe.url };
            }
            // "/dashboard/overview" is the default landing; PageGuard relocates to the first
            // available dashboard page once workspace data is resolved.
            return { type: "internal", url: safe?.url ?? "/dashboard/overview" };
        }

        // Direct entry to /login (no OAuth context in URL) cannot complete the token exchange.
        // Seed the OAuth flow so AuthGuard's IdP round-trip brings the user back here with
        // client_id/redirect_uri/state, while preserving any returnUrl (e.g. the forum page) in
        // pendingAuth so the callback can return the user there afterwards.
        return { type: "seed", returnUrl: returnUrl ?? "/dashboard/overview" };
    }, [hasValidSession, searchParams]);

    useEffect(() => {
        if (redirect?.type === "seed") {
            void authService.initiateAuthFlow(redirect.returnUrl).catch(() => setSeedError(true));
        }
    }, [redirect]);

    if (seedError) {
        return <TechnicalDifficultiesFallback />;
    }

    if (redirect && redirect.type === "internal") {
        return <Navigate to={redirect.url} replace />;
    }
    if (redirect) {
        // OAuth seed redirect is in flight (handled by the effect above).
        // Show a spinner — matching CallbackPage — so the user doesn't see a blank page.
        return <CircularProgressBox size="large" />;
    }

    const isPasswordMode = authMode === "password";
    const isTwoFactorMode = authMode === "2fa";

    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.email) {
            errors.email = t("pages:loginPage.validation.emailRequired");
        } else if (!emailRegex.test(values.email)) {
            errors.email = t("pages:loginPage.validation.invalidEmail");
        }

        if (!values.password) {
            errors.password = t("pages:loginPage.validation.passwordRequired");
        }

        if (values.mode === "2fa" && !values.totp) {
            errors.password = t("pages:loginPage.validation.authCodeRequired");
        }

        return errors;
    };

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            if (values.mode === "password") {
                const response = await authService.initiateLogin(values.email, values.password);
                if (response.model?.requiresTwoFactor) {
                    setAuthMode("2fa");
                    form.restart({ ...values, mode: "2fa" });
                    return;
                }
                await authService.continueAuthFlowAfterLogin();
                return;
            }

            if (values.mode === "2fa") {
                await authService.submitTotpToLogin(values.totp);
                authService.setIsUserTotpValid(true);
                await authService.continueAuthFlowAfterLogin();
            }
        } catch (error) {
            const err = unpackError(error);
            if (values.mode === "2fa") {
                if (error instanceof LoginRequestError && error.status === 400) {
                    authService.storeLoginNotice("pages:loginPage.toasts.sessionExpired");
                    await authService.restartLoginFlow();
                    return;
                }
                authService.setIsUserTotpValid(false);
                showErrorToast(err.message || t("pages:loginPage.toasts.invalid2FA"));
            } else {
                showErrorToast(err.message || t("common:toast.generalError"));
            }
        }
    };

    return (
        <AuthFlowContainer>
            <Box className={classes.formBox}>
                <Typography variant="h5">
                    {isPasswordMode ? t("common:button.login") : t("pages:loginPage.multiFactorTitle")}
                </Typography>

                <Form<FormValues>
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    validateOnBlur={false}
                    render={({ handleSubmit, submitting, form }) => (
                        <form onSubmit={handleSubmit}>
                            {isPasswordMode ? (
                                <Box className={classes.formContainer}>
                                    <TextField
                                        autoComplete="on"
                                        placeholder={t("pages:loginPage.emailPlaceholder")}
                                        name="email"
                                        type="text"
                                        showErrorOnSubmit
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <i className="fas fa-fw fa-envelope" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                    <Box className={classes.passwordContainer}>
                                        <PasswordField
                                            placeholder={t("common:label.password")}
                                            name="password"
                                            showErrorOnSubmit
                                            fullWidth
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <i className="fas fa-fw fa-key" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />

                                        <Link to="/account/recover" className={classes.forgotPasswordLink}>
                                            {t("pages:loginPage.forgotPassword")}
                                        </Link>
                                    </Box>

                                    <PrimaryButton type="submit" disabled={submitting} loading={submitting} fullWidth>
                                        {t("common:button.login")}
                                    </PrimaryButton>
                                </Box>
                            ) : null}
                            {isTwoFactorMode ? (
                                <Box className={classes.formContainer}>
                                    <OtpSection
                                        submitting={submitting}
                                        onSubmit={(otp) => {
                                            form.change("totp", otp);
                                            void handleSubmit();
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </form>
                    )}
                />

                {isPasswordMode && features.registrationEnabled ? (
                    <Typography className={classes.footerText}>
                        <Trans
                            ns="pages"
                            i18nKey="loginPage.dontHaveAccountWithLink"
                            components={{
                                Link: (
                                    <ApmLink
                                        role="link"
                                        parent="Login page"
                                        title="Sign up"
                                        to="/register"
                                        className={classes.signUpLink}
                                    />
                                ),
                            }}
                        />
                    </Typography>
                ) : null}
            </Box>
        </AuthFlowContainer>
    );
};

export default withMetaDescription(LoginPage, {
    title: i18next.t("pages:loginPage.metaTitle"),
    description: i18next.t("pages:loginPage.metaDescription"),
});
